import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
} from '@mui/material';
import { useForm } from 'react-typed-form';
import { FieldTextRequired } from '../../form';
import { useTypedDispatch } from '../../reducers';
import { useApiCallable } from '../../api';
import { ApiError } from '../../api/sustain-api-request';
import { ErrorList } from '../../components';

type Props = {
  phoneDigits: string;
};

export default function VerificationForm({ phoneDigits }: Props) {
  const callApi = useApiCallable();
  const dispatch = useTypedDispatch();

  const form = useForm({
    defaultValues: {
      authCode: '',
    },
    onSubmit: async (values) => {
      const response = await callApi<
        {
          user: {
            id: string;
            email: string;
            role?: string[];
            timezone?: string;
          };
        },
        any
      >(
        '/2fa_check',
        {
          method: 'POST',
          jsonBody: values,
        },
        {
          errorHandler: (error, originalHandler) => {
            if (error instanceof ApiError && error.response.status === 400) {
              form.addSubmitError('_form', 'Incorrect verification code.');
            }
            originalHandler(error);
          },
        },
      );

      if (!response) return;

      const sessionId = (response.headers as any).get('sustain-session-id');

      if (!sessionId) {
        alert('No session id');
        return;
      }

      const { user } = response.data;

      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            id: user.id,
            email: user.email,
            sessionId,
            roles: user.role ?? [],
            timezone: user.timezone ?? '',
          },
        },
      });
    },
  });

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        form.handleSubmit();
      }}
    >
      <Card>
        <Stack gap={1}>
          <CardHeader title="Enter your verification code" sx={{ pb: 0 }} />
          <Stack paddingX={3}>
            <Typography variant="body2" color="text.secondary">
              We've sent a one-time verification code via SMS to your mobile
              number
              {phoneDigits && ` ********${phoneDigits}`}
            </Typography>
          </Stack>
        </Stack>

        <CardContent>
          <Stack spacing={2}>
            <FieldTextRequired
              type="number"
              label="Code"
              field={form.getField('authCode')}
            />
            <Button variant="contained" type="submit" disabled={form.isLoading}>
              Submit
            </Button>
            <ErrorList errors={form.lastErrors._form} />
          </Stack>
        </CardContent>
      </Card>
    </form>
  );
}
